// src/pages/NewsDetails/NewsDetails.js

import React, { Fragment, useMemo } from "react";
import { Container } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import newsData from "../../data/newsData";
import { formatNewsDate } from "../../utils/newsHelpers";
import "./NewsDetailsPage.css";

function ParagraphRenderer({ segments = [] }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "link") {
          const isInternal = segment.href?.startsWith("/");

          if (isInternal) {
            return (
              <Link
                key={index}
                to={segment.href}
                target={segment.target || "_self"}
                className="news-details__inline-link"
              >
                {segment.value}
              </Link>
            );
          }

          return (
            <a
              key={index}
              href={segment.href}
              target={segment.target || "_blank"}
              rel="noreferrer"
              className="news-details__inline-link"
            >
              {segment.value}
            </a>
          );
        }

        return <Fragment key={index}>{segment.value}</Fragment>;
      })}
    </>
  );
}

export default function NewsDetailsPage() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");

  const newsItem = useMemo(
    () => newsData.find((item) => item.slug === slug),
    [slug]
  );

  if (!newsItem) {
    return (
      <section className="news-details">
        <Container>
          <div className="news-details__not-found">
            <h2>{isArabic ? "الخبر غير موجود" : "News article not found"}</h2>
            <Link to="/news" className="news-details__back-link">
              {isArabic ? "العودة إلى الأخبار" : "Back to News"}
            </Link>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="news-details">
      <Container>
        <div className="news-details__header">
          <Link to="/news" className="lk-brand-link">
            {isArabic ? "← العودة إلى الأخبار" : "← Back to News"}
          </Link>

          <div className="news-details__date">
            {formatNewsDate(newsItem.date, isArabic)}
          </div>

          <h1 className="news-details__title">
            {isArabic ? newsItem.title.ar : newsItem.title.en}
          </h1>

          <p className="news-details__excerpt">
            {isArabic ? newsItem.excerpt.ar : newsItem.excerpt.en}
          </p>
        </div>

        <div className="news-details__featured-wrap">
          <img
            src={newsItem.featuredImage}
            alt={isArabic ? newsItem.featuredAlt.ar : newsItem.featuredAlt.en}
            className="news-details__featured-image"
          />
        </div>

        <div className="news-details__content">
          {newsItem.contentBlocks.map((block, index) => {
            if (block.type === "image") {
              return (
                <div key={index} className="news-details__image-block">
                  <img
                    src={block.src}
                    alt={isArabic ? block.alt.ar : block.alt.en}
                    className="news-details__content-image"
                  />
                </div>
              );
            }

            if (block.type === "paragraph") {
              return (
                <p key={index} className="news-details__paragraph">
                  <ParagraphRenderer
                    segments={isArabic ? block.content.ar : block.content.en}
                  />
                </p>
              );
            }

            return null;
          })}
        </div>
      </Container>
    </section>
  );
}